import { useState } from 'react'
import { api, ApiError } from '../../lib/api'
import { useCountdown } from '../../lib/clock'
import { CLOCK_BAR, CLOCK_TEXT } from '../../lib/clockTheme'
import { clockFraction, clockTone, formatClock, nextPickForTeam } from '../../lib/draftView'
import type { ClockTone } from '../../lib/draftView'
import type { DraftResponse, DraftState, TeamState } from '../../types/protocol'

// The one piece of the draft room that never scrolls. On a phone the clock is
// the only thing on screen with a deadline attached, so it stays pinned while
// the pool scrolls under it — the old layout let it leave the viewport the
// moment you started browsing.

interface Props {
  draft: DraftState
  myTeam: TeamState | undefined
  teamToken: string | null
}

export default function DraftHeader({ draft, myTeam, teamToken }: Props) {
  // Every hook runs before any early return. Bailing out first would change
  // the hook count between renders (setup -> live) and corrupt React's
  // bookkeeping for the whole subtree.
  const deadlineMs = draft.deadline_at ? new Date(draft.deadline_at).getTime() : null
  const { remainingMs, expired } = useCountdown(deadlineMs)

  const currentPick = draft.picks.find((p) => p.overall === draft.current_pick_overall)
  const onClockTeam = currentPick ? draft.teams.find((t) => t.id === currentPick.team_id) : null
  const isMyPick = draft.my_team_id != null && currentPick?.team_id === draft.my_team_id

  const upNext = draft.picks
    .filter((p) => p.status === 'pending' && p.overall > (draft.current_pick_overall ?? 0))
    .sort((a, b) => a.overall - b.overall)
    .slice(0, 2)
    .map((p) => draft.teams.find((t) => t.id === p.team_id)?.name)
    .filter((name): name is string => !!name)

  const myNext = nextPickForTeam(draft.picks, draft.current_pick_overall, draft.my_team_id)

  // Paused holds the frozen remainder; the wait policy drops the deadline
  // entirely but still owes a pick, which reads as expired rather than idle.
  const paused = draft.status === 'paused'
  const waiting = draft.status === 'live' && deadlineMs == null && currentPick != null
  const pausedMs = draft.paused_remaining_ms ?? draft.pick_seconds * 1000

  let clockText: string
  let tone: ClockTone
  if (draft.status === 'complete') {
    clockText = 'Done'
    tone = 'normal'
  } else if (draft.status === 'setup') {
    clockText = '--:--'
    tone = 'normal'
  } else if (paused) {
    clockText = formatClock(pausedMs)
    tone = 'warn'
  } else if (waiting) {
    clockText = '0:00'
    tone = 'crit'
  } else {
    clockText = formatClock(remainingMs)
    tone = clockTone(remainingMs, expired)
  }

  const fraction = paused
    ? clockFraction(pausedMs, draft.pick_seconds)
    : clockFraction(remainingMs, draft.pick_seconds)

  return (
    // Not sticky itself — Draft.tsx pins this and the tab bar together as one
    // unit, so neither has to know the other's height.
    //
    // Your turn changes the whole bar, not a sentence in it: the surface warms,
    // a solid rail lights up the left edge, and the eyebrow starts pulsing.
    // That is the only place --color-turn appears in the app.
    <header
      className={`border-b shadow-raised backdrop-blur-sm ${
        isMyPick
          ? 'border-turn/40 bg-turn/10 shadow-[inset_3px_0_0_var(--color-turn),inset_0_1px_0_rgb(255_255_255/0.07),0_1px_2px_rgb(0_0_0/0.5)]'
          : 'border-line bg-surface-2/95'
      }`}
    >
      <div className="mx-auto w-full max-w-7xl px-3 py-2.5 sm:px-5 sm:py-3">
        <div className="flex items-baseline justify-between gap-3">
          {isMyPick ? (
            <span className="animate-board-pulse eyebrow text-turn motion-reduce:animate-none">
              ● You&apos;re on the clock
            </span>
          ) : (
            <span className="truncate text-xs font-medium text-ink-mid">{draft.name}</span>
          )}
          <span className="shrink-0 font-mono text-[11px] font-medium tracking-[0.18em] text-signal">
            {draft.join_code}
          </span>
        </div>

        <div className="mt-2 flex items-end justify-between gap-3">
          <div className="min-w-0">
            <p className={`eyebrow ${isMyPick ? 'text-turn' : ''}`}>
              {isMyPick ? 'Your pick' : paused ? 'Paused' : 'On the clock'}
            </p>
            {currentPick ? (
              <>
                {!isMyPick && (
                  <p className="mt-0.5 truncate font-display text-2xl leading-none font-bold text-ink-hi">
                    {onClockTeam?.name ?? 'Unknown team'}
                  </p>
                )}
                <p className="mt-1 font-mono text-[11px] text-ink-lo tabular-nums">
                  R{currentPick.round} · Pick {currentPick.slot}
                  {waiting && ' · time expired, still their pick'}
                </p>
              </>
            ) : (
              <p className="mt-0.5 truncate font-display text-2xl leading-none font-bold text-ink-hi">
                {draft.status === 'complete' ? 'Draft complete' : 'Not started'}
              </p>
            )}
          </div>

          <p
            className={`shrink-0 font-display text-[42px] leading-[0.85] font-bold tabular-nums ${CLOCK_TEXT[tone]}`}
          >
            {clockText}
          </p>
        </div>

        {/* The drain. Recessed rather than raised — it is a track cut into the
            header, so the inset shadow reads as depth below the surface. */}
        <div className="mt-2.5 h-[3px] overflow-hidden rounded-full bg-ink shadow-[inset_0_1px_1px_rgb(0_0_0/0.6)]">
          <div
            className={`h-full rounded-full transition-[width] duration-200 ease-linear ${CLOCK_BAR[tone]}`}
            style={{ width: `${fraction * 100}%` }}
          />
        </div>

        <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-[11px] text-ink-lo">
          <span className="truncate">
            {upNext.length > 0 ? (
              <>
                {isMyPick ? 'Then ' : 'On deck '}
                <span className="font-medium text-ink-mid">{upNext.join(' → ')}</span>
              </>
            ) : (
              ' '
            )}
          </span>

          <span className="flex shrink-0 items-center gap-3">
            {myNext && myNext.picksAway > 0 && (
              <span>
                You pick{' '}
                <span className="font-mono font-medium text-ink-mid">
                  R{myNext.pick.round}.{myNext.pick.slot}
                </span>{' '}
                · {myNext.picksAway} away
              </span>
            )}
            {myTeam && teamToken && (
              <AutopickToggle draft={draft} team={myTeam} teamToken={teamToken} />
            )}
          </span>
        </div>
      </div>
    </header>
  )
}

// Autopick lives in the header because its state is only ever load-bearing at
// 0:00 — the same moment the clock beside it is the thing being watched.
function AutopickToggle({
  draft,
  team,
  teamToken,
}: {
  draft: DraftState
  team: TeamState
  teamToken: string
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const enabled = team.autopick_enabled

  async function toggle(next: boolean) {
    setBusy(true)
    setError(null)
    try {
      await api<DraftResponse>(`/drafts/${draft.join_code}/teams/${team.id}/autopick`, {
        method: 'PATCH',
        body: { enabled: next },
        teamToken,
      })
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not change autopick')
    } finally {
      setBusy(false)
    }
  }

  return (
    <label
      title={
        enabled
          ? 'At 0:00 your queue picks for you, then best available.'
          : 'Turn on to have your queue pick for you at 0:00.'
      }
      className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-2 py-0.5 ${
        enabled
          ? 'border-signal/50 bg-signal/10 text-signal'
          : 'border-line-strong text-ink-lo hover:border-ink-faint hover:text-ink-mid'
      } ${busy ? 'opacity-50' : ''}`}
    >
      <input
        type="checkbox"
        className="h-3 w-3 accent-signal"
        checked={enabled}
        disabled={busy}
        onChange={(event) => void toggle(event.target.checked)}
      />
      Autopick
      {error && <span className="text-crit">· {error}</span>}
    </label>
  )
}
